import BreadCrumbs from "@/components/Gallery/BreadCrumbs";
import Button, { ButtonType } from "@/components/reusable/Button";
import GenericPagination from "@/components/shared/GenericPagination";
import { getGalleryUrl } from "@/lib/gallery";
import Image from "next/image";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface GalleryCategoryData {
  categories: {
    id?: string;
    title: string;
    slug: string;
    category?: string;
  }[];
  totalPages?: number;
  currentPage?: number;
}

interface GalleryIndexProps {
  data: GalleryCategoryData;
}

export default function GalleryIndex({ data }: GalleryIndexProps) {
  const galleryBaseUrl = `/${process.env.NEXT_PUBLIC_GALLERY_SLUG}`;

  // Animation variants for slide-in from left and right
  const slideInLeft = {
    hidden: {
      opacity: 0,
      x: -100,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
  };

  const slideInRight = {
    hidden: {
      opacity: 0,
      x: 100,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
  };

  // Animation variants for content inside
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.3,
      },
    },
  };

  // Group categories by their category type
  const groupedCategories = {
    face: data.categories.filter((cat) => cat.category === "face"),
    body: data.categories.filter((cat) => cat.category === "body"),
    breast: data.categories.filter((cat) => cat.category === "breast"),
  };

  // Category group configuration with images
  const categoryGroups = [
    {
      key: "face",
      title: "Face",
      image:
        "https://res.cloudinary.com/dwz1f28wj/image/upload/c_crop,ar_1:1/v1753152356/Face_bridge_araotd.webp",
      categories: groupedCategories.face,
    },
    {
      key: "body",
      title: "Body",
      image:
        "https://res.cloudinary.com/dwz1f28wj/image/upload/c_crop,ar_1:1/v1753152372/body_bridge_horizontal_c5pwol.webp",
      categories: groupedCategories.body,
    },
    {
      key: "breast",
      title: "Breast",
      image:
        "https://res.cloudinary.com/dwz1f28wj/image/upload/c_crop,ar_1:1/v1753152357/Breast_bridge_ubo9di.webp",
      categories: groupedCategories.breast,
    },
  ];

  return (
    <LazyMotion features={domAnimation}>
      <>
        <BreadCrumbs
        className="bg-accent-color-three-dark text-[#fff] py-6 px-6 md:py-6 md:px-12 lg:py-8 lg:px-32 m-0 text-span-medium"
        segments={[]}
      />
      <section id="gallery-index" className="bg-accent-color-three">
        <div className="container mx-auto max-w-[2400px]">
          <div className="flex flex-col gap-12 lg:gap-20">
            {/* Grouped Categories */}
            <div className="flex flex-col md:flex-row md:flex-wrap justify-center">
              {categoryGroups.map((group, index) => (
                <m.div
                  key={group.key}
                  className="flex-1 max-w-[950px] min-h-[450px]"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={index % 2 === 0 ? slideInLeft : slideInRight}
                  transition={{ delay: index * 0.2 }}
                >
                  <div className="flex flex-col md:flex-row h-full">
                    {/* Category Group Image and Title with hoverable category list */}
                    <div className="relative overflow-hidden aspect-square md:min-w-[475px] group">
                      <Image
                        src={group.image}
                        alt={group.title}
                        fill
                        className="object-cover transition-opacity duration-300 group-hover:opacity-0"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                      <m.div
                        className="absolute bg-accent-color-three flex bottom-16 left-12 w-fit h-fit py-4 px-8 z-10 transition-opacity duration-300 group-hover:opacity-0"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              duration: 0.8,
                              ease: "easeOut",
                              delay: 0.4,
                            },
                          },
                        }}
                      >
                        <h2 className="text-white uppercase text-center text-[18px] leading-6 tracking-[0.10em]">
                          {group.title}
                        </h2>
                      </m.div>
                      {/* Category List on hover */}
                      <m.div
                        className="absolute inset-0 flex flex-col justify-center items-center bg-accent-color-three transition-opacity duration-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto z-20"
                        initial={false}
                        animate={false}
                      >
                        <m.span
                          className="text-white text-h2-mobile md:text-h2-tablet lg:text-h2-desktop font-serif mb-8"
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                          variants={fadeInUp}
                        >
                          {group.title}
                        </m.span>
                        <m.div
                          className="flex flex-wrap gap-3"
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                          variants={{
                            hidden: { opacity: 0 },
                            visible: {
                              opacity: 1,
                              transition: {
                                delay: 0.5,
                                staggerChildren: 0.1,
                              },
                            },
                          }}
                        >
                          {group.categories.map((category) => (
                            <m.div
                              key={category.id}
                              variants={{
                                hidden: { opacity: 0, scale: 0.8 },
                                visible: {
                                  opacity: 1,
                                  scale: 1,
                                  transition: {
                                    duration: 0.4,
                                    ease: "easeOut",
                                  },
                                },
                              }}
                            >
                              <Button
                                type={ButtonType.galleryOutline}
                                rounded
                                link={`${galleryBaseUrl}/${category.slug}`}
                                className="px-4 py-1 text-[16px] leading-6 tracking-[0.15em] min-h-0"
                              >
                                {category.title}
                              </Button>
                            </m.div>
                          ))}
                        </m.div>
                      </m.div>
                    </div>
                    {/* Remove the old Category List from here */}
                  </div>
                </m.div>
              ))}
            </div>
            {data.totalPages && data.totalPages > 1 && (
              <GenericPagination
                currentPage={data.currentPage || 1}
                totalPages={data.totalPages}
                baseUrl={galleryBaseUrl}
                pageSlug={process.env.NEXT_PUBLIC_GALLERY_PAGE_SLUG || "page"}
              />
            )}
          </div>
        </div>
      </section>
    </>
    </LazyMotion>
  );
}
